import {Trans, useTranslation} from 'react-i18next'
import {
  Box,
  Flex,
  Text,
  Button,
  Heading,
  Divider,
  Link,
  IconButton,
  useDisclosure,
} from '@chakra-ui/react'
import {useDismissRewardsBanner} from '@paymentsOnDemand/public'
import LogoQantasBusinessRewards from '../Logo/LogoQantasBusinessRewards'
import LogoVirginVelocityFrequentFlyer from '../Logo/LogoVirginVelocityFrequentFlyer'
import BrandedBackgroundPattern from '../../../../assets/images/reward_program/branded_background_pattern.svg'
import {useSupplierRewardsProgramLearnMoreModal} from '../../contexts/supplierRewardsProgramLearnMoreModal'
import useFeatureCookie from '../../hooks/useFeatureCookie'
import {CrossIcon} from '../../icons'
import DismissRewardsBannerModal from './DismissRewardsBannerModal'

// "Hide for now" persists a per-user, per-supplier cookie that expires after 24 hours.
const HIDE_FOR_NOW_EXPIRY_SECONDS = 24 * 60 * 60

const SupplierRewardsBanner = () => {
  const {t} = useTranslation('onboarding')
  const {onOpen} = useSupplierRewardsProgramLearnMoreModal()
  const dismissModal = useDisclosure()
  const {dismiss, isDismissing} = useDismissRewardsBanner()
  const [hiddenForNow, hideForNow] = useFeatureCookie(
    'rewards_dashboard_banner_dismissed',
    {expiry: HIDE_FOR_NOW_EXPIRY_SECONDS},
  )

  const onHideForNow = () => {
    hideForNow()
    dismissModal.onClose()
  }

  const onNeverShowAgain = async () => {
    await dismiss()
    dismissModal.onClose()
  }

  if (hiddenForNow) {
    return null
  }

  return (
    <Box
      className="fs-unmask"
      position="relative"
      data-testid="supplier-rewards-banner"
      mb="md"
    >
      {/* Dismiss (close) icon */}
      <IconButton
        variant="unstyled"
        aria-label={t('supplierRewardsBanner.dismiss')}
        icon={<CrossIcon boxSize="20px" />}
        onClick={dismissModal.onOpen}
        data-testid="dismiss-supplier-rewards-banner"
        position="absolute"
        top="sm"
        right="sm"
        zIndex={4}
        minW="auto"
        h="20px"
        display="flex"
        alignItems="center"
        color="mld.neutral.900"
      />

      <DismissRewardsBannerModal
        isOpen={dismissModal.isOpen}
        onClose={dismissModal.onClose}
        onHideForNow={onHideForNow}
        onNeverShowAgain={onNeverShowAgain}
        isDismissing={isDismissing}
      />

      {/* Main Banner Container */}
      <Flex
        background="mld.primary.400"
        border="1px solid"
        borderColor="mld.primary.400"
        borderRadius="2px"
        overflow="hidden"
        position="relative"
        direction={{base: 'column', xl: 'row'}}
        h="full"
      >
        {/* Branded percent-sign bubble asset, rotated to sweep across the banner
            background. Rendered at the asset's natural 1069:1217 ratio and placed
            to mirror the Figma frame so the bubbles stay round rather than stretched. */}
        <Box
          as="img"
          src={BrandedBackgroundPattern}
          alt=""
          aria-hidden="true"
          position="absolute"
          top={{base: '-160px', md: '-275px'}}
          left={{base: '-200px', md: '-407px'}}
          w={{base: '600px', md: '1069px'}}
          h={{base: '683px', md: '1217px'}}
          transform="rotate(-87.72deg)"
          transformOrigin="center"
          pointerEvents="none"
          zIndex={1}
        />

        {/* Orange left section with decorative bubbles */}
        <Flex
          minW={{base: 'full', md: '464px'}}
          zIndex={2}
          justifyContent="flex-start"
          alignItems="center"
          p={{base: 'md', md: '0'}}
        >
          {/* Content centered using flexbox */}
          <Box
            p={4}
            pt={{base: '6', md: '12'}}
            pl={{base: '0', md: '10'}}
            display="flex"
            flexDirection="column"
            alignItems={{base: 'flex-start', md: 'flex-start'}}
            textAlign="left"
          >
            {/* Main content */}
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              padding="0px"
              gap="sm"
              textAlign="left"
              maxW="299px"
            >
              {/* Marmalade Rewards Program Title */}
              <Heading
                fontWeight={700}
                fontSize="2xl"
                lineHeight="90%"
                display="flex"
                alignItems="center"
                verticalAlign="middle"
                letterSpacing="-1.8px"
                textAlign="left"
                width="100%"
              >
                {t('supplierRewardsBanner.title')}
              </Heading>

              {/* Message */}
              <Text lineHeight="150%" letterSpacing="-0.02em" textAlign="left">
                {t('supplierRewardsBanner.description')}
              </Text>
            </Box>
          </Box>
        </Flex>

        {/* Frosted-glass content panel */}
        <Box
          flex={{base: 'none', md: 1}}
          width={{base: '100%', md: 'auto'}}
          bg="rgba(255, 255, 255, 0.8)"
          backdropFilter="blur(3px)"
          position="relative"
          zIndex={3}
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          overflow="hidden"
          p="6"
          px={{base: '6', md: '10'}}
        >
          {/* Main content container */}
          <Flex
            direction="column"
            align="flex-start"
            gap={{base: '4', md: '6'}}
          >
            {/* Logo Section - Top */}
            <Heading
              fontSize="sm"
              fontWeight={500}
              textTransform="uppercase"
              display={{base: 'block', md: 'none'}}
            >
              <Text display="block" as="span" fontWeight={400} mb="sm">
                {t('supplierRewardsBanner.partnership')},
              </Text>
              <Text display="block" as="span" fontWeight={500}>
                {t('supplierRewardsBanner.listOfPartners')}
              </Text>
            </Heading>

            {/* Content Section */}
            <Box flex={1} mt={{base: '0', md: '0'}}>
              <Flex
                justify={{base: 'flex-start', md: 'flex-end'}}
                align="center"
                width="100%"
                mb="sm"
              >
                <Flex gap="sm" align="center">
                  {/* Airline Logos */}
                  <Flex
                    align="center"
                    direction={{base: 'row', md: 'row'}}
                    justify={{base: 'center', md: 'flex-start'}}
                  >
                    <LogoQantasBusinessRewards
                      height={{base: '36px', md: '33.33px'}}
                      width="130px"
                    />
                    <LogoVirginVelocityFrequentFlyer
                      height={{base: '36px', md: '44px'}}
                      width="130px"
                    />
                  </Flex>
                </Flex>
              </Flex>

              <Divider
                borderColor="mld.neutral.1000"
                mt="md"
                mb="md"
                display={{base: 'block', md: 'none'}}
              />

              <Heading
                fontSize="sm"
                lineHeight="110%"
                textTransform="uppercase"
                mb="sm"
                display={{base: 'none', md: 'block'}}
              >
                <Text display="block" as="span" fontWeight={400} mb="xs">
                  {t('supplierRewardsBanner.partnership')},
                </Text>
                <Text display="block" as="span" fontWeight={500}>
                  {t('supplierRewardsBanner.listOfPartners')}
                </Text>
              </Heading>
              <Text lineHeight="150%">
                <Trans
                  t={t}
                  i18nKey="supplierRewardsBanner.partnershipDescription"
                  components={{
                    termsLink: (
                      <Link
                        isExternal
                        variant="primary"
                        href="https://www.marmalade.com.au/marmalade-rewards-conversion-partner-terms-and-conditions"
                        textStyle="body-copy"
                      />
                    ),
                  }}
                />
              </Text>
            </Box>

            {/* Bottom Section - Buttons */}
            <Flex
              gap="4"
              mt={{base: '2', md: '0'}}
              align="center"
              flexDirection={{base: 'column', md: 'row'}}
              alignItems="flex-start"
            >
              <Button
                variant="primary"
                as={Link}
                textTransform="uppercase"
                href="/settings/rewards"
              >
                {t('supplierRewardsBanner.buttons.enableRewards')}
              </Button>

              <Button
                variant="secondary"
                backgroundColor="transparent"
                as="button"
                onClick={onOpen}
                textTransform="uppercase"
                sx={{
                  '&:hover': {
                    backgroundColor: 'transparent',
                    textDecoration: 'underline',
                  },
                  '&:focus': {
                    backgroundColor: 'transparent',
                  },
                  '&:active': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {t('supplierRewardsBanner.buttons.learnMore')}
              </Button>
            </Flex>
          </Flex>
        </Box>
      </Flex>
    </Box>
  )
}

export default SupplierRewardsBanner
